import { Alert, Spin } from 'antd';
import { Profile } from 'oidc-client';
import React, { useEffect, useMemo, useState } from 'react';
import { defaultSelectedUserRole, getUserRole } from '../../helpers/CheckPermissionHelper';
import { MatchVm } from '../../utils/api';
import './insights.css';
import { canAccessInsights, canUseInsightsPages } from './insightsAccess';
import { clubLabel, teamLabel, useInsightsClub } from './insightsClubScope';
import { isFinishedMatch, MatchResult, ourTeamIds, perGame } from './insightsClubSummary';
import { buildGameStateSplits, mergeGameStateSplits, shootPct } from './insightsGameState';
import InsightsLegend from './InsightsLegend';
import {
    collectBundleFilterOptions,
    emptyMatchBundleFilters,
    filterMatchBundles,
    MatchBundleFilterSelection,
} from './insightsMatchBundleFilters';
import InsightsNav from './InsightsNav';
import InsightsPlayerTrendChart from './InsightsPlayerTrendChart';
import { fetchClubMatches, loadMatchBundles, MatchBundle } from './insightsQueries';
import InsightsScopeFilters from './InsightsScopeFilters';
import {
    ATTACK_LABELS,
    ATTACK_ORDER,
    AttackType,
    buildPeriodSplits,
    buildQuarterControl,
    buildRecentVsSample,
    buildTeamScoreForm,
    buildTeamTrendSeries,
    buildTurnoverTypeRows,
    buildVenueEventRows,
    buildVenueSplits,
    buildWhyWeWin,
    buildWinCorrelates,
    conversionPercent,
    gkSitPct,
    isPercentTrend,
    pctText,
    PeriodSplit,
    SampleFormRow,
    tallyBundles,
    TEAM_TREND_METRICS,
    TeamTrendMetric,
    wdlText,
} from './insightsTeamSplits';
import InsightsTopbar from './InsightsTopbar';

interface Props {
    userProfile?: Profile;
}

const PAGE_SIZE = 100;

const percentText = (goals: number, shots: number): string => {
    const value = shots > 0 ? Math.round((goals / shots) * 100) : undefined;
    return pctText(value);
};

const formPips = (results: MatchResult[]): React.ReactNode => {
    if (results.length === 0) return <b>-</b>;
    return (
        <div className="insightsFormRow">
            {results.map((result, index) => (
                <span
                    key={`${result}-${index}`}
                    className={`insightsFormPip is-${result.toLowerCase()}`}
                >
                    {result}
                </span>
            ))}
        </div>
    );
};

const formatFormDelta = (row: SampleFormRow): string => {
    if (Math.abs(row.delta) < 0.05) return '0';
    const sign = row.delta > 0 ? '+' : '';
    if (row.label.includes('%')) return `${sign}${Math.round(row.delta)}pp`;
    return `${sign}${row.delta.toFixed(1)}`;
};

const formDeltaClass = (row: SampleFormRow): string => {
    const good = row.lowerIsBetter ? row.delta < 0 : row.delta > 0;
    const bad = row.lowerIsBetter ? row.delta > 0 : row.delta < 0;
    if (good) return 'isUp';
    if (bad) return 'isDown';
    return 'isFlat';
};

const InsightsTeam: React.FC<Props> = ({ userProfile }) => {
    // getUserRole returns a new object every call; putting it in effect deps
    // retriggered the page fetch in a tight loop.
    const userRole = useMemo(
        () => (userProfile ? getUserRole(userProfile) : defaultSelectedUserRole),
        [userProfile]
    );
    const allowed = canAccessInsights(userRole);

    const scope = useInsightsClub(userProfile, allowed && canUseInsightsPages(userRole));
    const { selectedClubId, selectedTeamId } = scope;

    const [allBundles, setAllBundles] = useState<MatchBundle[]>([]);
    const [finishedMatches, setFinishedMatches] = useState<MatchVm[]>([]);
    const [filters, setFilters] = useState<MatchBundleFilterSelection>(emptyMatchBundleFilters());
    const [fetchedTotal, setFetchedTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [trendMetric, setTrendMetric] = useState<TeamTrendMetric>('shootingPct');

    const teamIds = useMemo(
        () => ourTeamIds(scope.teams, selectedTeamId),
        [scope.teams, selectedTeamId]
    );

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            if (
                !allowed ||
                !canUseInsightsPages(userRole) ||
                selectedClubId == null ||
                teamIds.length === 0
            ) {
                setFinishedMatches([]);
                setAllBundles([]);
                setFetchedTotal(0);
                return;
            }

            setLoading(true);
            setError(undefined);
            try {
                const page = await fetchClubMatches({
                    clubId: selectedClubId,
                    teamId: selectedTeamId,
                    pageSize: PAGE_SIZE,
                });
                if (cancelled) return;

                const finished = (page.pageItems || []).filter((match: MatchVm) =>
                    isFinishedMatch(match)
                );
                // ponytail: loads club finished set then filters client-side. Ceiling is
                // PAGE_SIZE; upgrade path is server season aggregate.
                const loaded = await loadMatchBundles(finished);
                if (cancelled) return;
                setFinishedMatches(finished);
                setAllBundles(loaded);
                setFetchedTotal(page.totalItems || finished.length);
                setFilters(emptyMatchBundleFilters());
            } catch {
                if (!cancelled) {
                    setFinishedMatches([]);
                    setAllBundles([]);
                    setFetchedTotal(0);
                    setError('Could not build team splits for this selection.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [allowed, userRole, selectedClubId, selectedTeamId, teamIds]);

    const filterOptions = useMemo(
        () => collectBundleFilterOptions(allBundles, teamIds),
        [allBundles, teamIds]
    );
    const filteredBundles = useMemo(
        () => filterMatchBundles(allBundles, filters),
        [allBundles, filters]
    );
    const filteredMatches = useMemo(() => {
        const ids = new Set(filteredBundles.map((bundle) => bundle.detail.id));
        return finishedMatches.filter((match) => ids.has(match.id));
    }, [finishedMatches, filteredBundles]);

    const venue = useMemo(
        () => (filteredMatches.length > 0 ? buildVenueSplits(filteredMatches, teamIds) : undefined),
        [filteredMatches, teamIds]
    );
    const scoreForm = useMemo(
        () => buildTeamScoreForm(filteredMatches, teamIds),
        [filteredMatches, teamIds]
    );
    const tally = useMemo(() => tallyBundles(filteredBundles, teamIds), [filteredBundles, teamIds]);
    const splits = tally.matches > 0 ? { us: tally.attack, them: tally.them } : undefined;
    const periods = useMemo(
        () => buildPeriodSplits(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const quarterControl = useMemo(
        () => buildQuarterControl(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const whyWeWin = useMemo(
        () => buildWhyWeWin(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const venueEvents = useMemo(
        () => buildVenueEventRows(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const turnoverRows = useMemo(() => buildTurnoverTypeRows(tally), [tally]);
    const recentForm = useMemo(
        () => buildRecentVsSample(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const correlates = useMemo(
        () => buildWinCorrelates(filteredBundles, teamIds),
        [filteredBundles, teamIds]
    );
    const trendSeries = useMemo(
        () => buildTeamTrendSeries(filteredBundles, teamIds, trendMetric),
        [filteredBundles, teamIds, trendMetric]
    );
    const gameState = useMemo(() => {
        if (filteredBundles.length === 0) return undefined;
        return mergeGameStateSplits(
            filteredBundles.map((bundle) => buildGameStateSplits(bundle, teamIds))
        );
    }, [filteredBundles, teamIds]);

    const shortfall =
        fetchedTotal > PAGE_SIZE
            ? `Showing latest ${PAGE_SIZE} finished matches of ${fetchedTotal} available.`
            : undefined;

    if (!canUseInsightsPages(userRole)) {
        return (
            <Alert
                type="warning"
                showIcon
                message="Insights is only on staging and the Statistics Portal"
            />
        );
    }

    if (!allowed) {
        return (
            <div className="insightsRoot">
                <InsightsTopbar />
                <div className="insightsBlocked">
                    <h1>Not available for this account</h1>
                    <p>Club Insights is for Federation and Club accounts that enter statistics.</p>
                </div>
            </div>
        );
    }

    const teamName = scope.teams.find((team) => team.id === selectedTeamId);
    const scopeLine = [
        clubLabel(scope.selectedClub) || 'No club',
        teamName ? teamLabel(teamName) : 'All teams',
    ]
        .filter(Boolean)
        .join(' · ');

    const penaltyKill = splits ? conversionPercent(splits.them.extraPlayer) : undefined;
    const showGameState =
        gameState &&
        (gameState.leading.shots > 0 ||
            gameState.tied.shots > 0 ||
            gameState.trailing.shots > 0 ||
            gameState.clutch.shots > 0);

    return (
        <div className="insightsRoot">
            <InsightsTopbar scope={scope} />

            <div className="insightsBody">
                <InsightsNav active="team" />
                <InsightsLegend />

                <InsightsScopeFilters
                    options={filterOptions}
                    filters={filters}
                    onChange={setFilters}
                    matchCount={filteredBundles.length}
                />

                <div className="insightsHero">
                    <div>
                        <h1>Team</h1>
                        <p>
                            How <strong>{scopeLine}</strong> actually plays in your logged matches.
                            Numbers follow the filters above.
                        </p>
                    </div>
                </div>

                {error && (
                    <Alert type="error" showIcon message={error} style={{ marginBottom: 14 }} />
                )}
                {shortfall && <p className="insightsTableNote">{shortfall}</p>}

                <Spin spinning={loading || scope.loading}>
                    <section className="insightsPanel">
                        <div className="insightsPanelHead">
                            <h2>Form</h2>
                            <span>Finished matches · newest first</span>
                        </div>
                        {filteredMatches.length === 0 ? (
                            <div className="insightsEmpty">
                                {allBundles.length === 0
                                    ? 'No finished matches yet.'
                                    : '0 matches for the current filters.'}
                            </div>
                        ) : (
                            <div className="insightsStatStrip">
                                <div className="insightsStat">
                                    {formPips(scoreForm.last10)}
                                    <i>Last 10</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{scoreForm.streak}</b>
                                    <i>Streak</i>
                                </div>
                                <div className="insightsStat">
                                    <b>
                                        {scoreForm.closePlayed === 0
                                            ? '-'
                                            : `${scoreForm.closeWins}-${scoreForm.closeDraws}-${scoreForm.closeLosses}`}
                                    </b>
                                    <i>Close ±1</i>
                                </div>
                                <div className="insightsStat">
                                    <b>
                                        {scoreForm.avgWinMargin == null
                                            ? '-'
                                            : scoreForm.avgWinMargin.toFixed(1)}
                                    </b>
                                    <i>Avg win margin</i>
                                </div>
                                <div className="insightsStat">
                                    <b>
                                        {scoreForm.avgLossMargin == null
                                            ? '-'
                                            : scoreForm.avgLossMargin.toFixed(1)}
                                    </b>
                                    <i>Avg loss margin</i>
                                </div>
                            </div>
                        )}
                    </section>

                    {splits || tally.sogFaced > 0 ? (
                        <>
                            <section className="insightsPanel">
                                <div className="insightsPanelHead">
                                    <h2>Special teams and attack types</h2>
                                    <span>
                                        {penaltyKill == null
                                            ? 'Penalty kill needs opponent extra-player shots'
                                            : `Penalty kill ${100 - penaltyKill}%`}
                                    </span>
                                </div>
                                {!splits ? (
                                    <div className="insightsEmpty">
                                        No shots logged in this sample.
                                    </div>
                                ) : (
                                    <div className="insightsTableScroll">
                                        <table className="insightsPlayerTable">
                                            <thead>
                                                <tr>
                                                    <th>Attack</th>
                                                    <th>Our goals</th>
                                                    <th>Our shots</th>
                                                    <th>Ours</th>
                                                    <th>Their goals</th>
                                                    <th>Their shots</th>
                                                    <th>Theirs</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ATTACK_ORDER.map((type: AttackType) => (
                                                    <tr key={type}>
                                                        <td>{ATTACK_LABELS[type]}</td>
                                                        <td>{splits.us[type].goals}</td>
                                                        <td>{splits.us[type].shots}</td>
                                                        <td>
                                                            {pctText(
                                                                conversionPercent(splits.us[type])
                                                            )}
                                                        </td>
                                                        <td>{splits.them[type].goals}</td>
                                                        <td>{splits.them[type].shots}</td>
                                                        <td>
                                                            {pctText(
                                                                conversionPercent(splits.them[type])
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <p className="insightsTableNote">
                                    Opponent columns describe how they played against you, not their
                                    season.
                                </p>
                            </section>

                            {tally.sogFaced > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Goalkeeping</h2>
                                        <span>SOG faced = saves + goals against</span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Situation</th>
                                                <th>Saves</th>
                                                <th>GA</th>
                                                <th>Save %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Overall</td>
                                                <td>{tally.saves}</td>
                                                <td>{tally.goalsAgainst}</td>
                                                <td>
                                                    {pctText(
                                                        tally.sogFaced > 0
                                                            ? Math.round(
                                                                  (tally.saves / tally.sogFaced) *
                                                                      100
                                                              )
                                                            : undefined
                                                    )}
                                                </td>
                                            </tr>
                                            {ATTACK_ORDER.map((type) => {
                                                const bucket = tally.gkBySit[type];
                                                if (bucket.saves + bucket.goalsAgainst === 0)
                                                    return null;
                                                return (
                                                    <tr key={type}>
                                                        <td>{ATTACK_LABELS[type]}</td>
                                                        <td>{bucket.saves}</td>
                                                        <td>{bucket.goalsAgainst}</td>
                                                        <td>{pctText(gkSitPct(bucket))}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </section>
                            ) : null}
                        </>
                    ) : null}

                    {tally.matches > 0 ? (
                        <section className="insightsPanel">
                            <div className="insightsPanelHead">
                                <h2>Swim-offs, turnovers, extras</h2>
                                <span>From the analysed matches</span>
                            </div>
                            <div className="insightsStatStrip">
                                <div className="insightsStat">
                                    <b>
                                        {tally.swimoffs === 0
                                            ? '-'
                                            : `${tally.swimoffWins}/${tally.swimoffs}`}
                                    </b>
                                    <i>Swim-off W</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{tally.turnovers}</b>
                                    <i>Turnovers</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{tally.steals}</b>
                                    <i>Steals</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{tally.blocks}</b>
                                    <i>Blocks</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{tally.exclusionsDrawn - tally.exclusionsCommitted}</b>
                                    <i>Ex net</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{tally.timeouts}</b>
                                    <i>Timeouts</i>
                                </div>
                                <div className="insightsStat">
                                    <b>{tally.cards}</b>
                                    <i>Cards</i>
                                </div>
                            </div>
                            {turnoverRows.length > 0 ? (
                                <table className="insightsPlayerTable insightsTableFit">
                                    <thead>
                                        <tr>
                                            <th>Turnover type</th>
                                            <th>Count</th>
                                            <th>/ g</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {turnoverRows.map((row) => (
                                            <tr key={row.label}>
                                                <td>{row.label}</td>
                                                <td>{row.count}</td>
                                                <td>
                                                    {tally.matches > 0
                                                        ? (row.count / tally.matches).toFixed(1)
                                                        : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : null}
                        </section>
                    ) : null}

                    {periods.length > 0 || quarterControl.length > 0 ? (
                        <div className="insightsTableCluster">
                            <section className="insightsPanel">
                                <div className="insightsPanelHead">
                                    <h2>By period</h2>
                                    <span>Totals in the sample</span>
                                </div>
                                {periods.length === 0 ? (
                                    <div className="insightsEmpty">
                                        No events logged in this sample.
                                    </div>
                                ) : (
                                    <div className="insightsTableScroll">
                                        <table className="insightsPlayerTable">
                                            <thead>
                                                <tr>
                                                    <th>Period</th>
                                                    <th>GF:GA</th>
                                                    <th>Shots</th>
                                                    <th>%</th>
                                                    <th>TO</th>
                                                    <th>ExC</th>
                                                    <th>ExD</th>
                                                    <th>EP</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {periods.map((split: PeriodSplit) => (
                                                    <tr key={split.periodId}>
                                                        <td>{split.period}</td>
                                                        <td>
                                                            {split.goalsFor}:{split.goalsAgainst}
                                                        </td>
                                                        <td>
                                                            {split.shotsFor}:{split.shotsAgainst}
                                                        </td>
                                                        <td>
                                                            {percentText(
                                                                split.goalsFor,
                                                                split.shotsFor
                                                            )}
                                                        </td>
                                                        <td>{split.turnoversFor}</td>
                                                        <td>{split.exclusionsCommitted}</td>
                                                        <td>{split.exclusionsDrawn}</td>
                                                        <td>
                                                            {split.extraPlayerShots === 0
                                                                ? '-'
                                                                : `${split.extraPlayerGoals}/${
                                                                      split.extraPlayerShots
                                                                  } (${percentText(
                                                                      split.extraPlayerGoals,
                                                                      split.extraPlayerShots
                                                                  )})`}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>

                            {quarterControl.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Quarter won → match result</h2>
                                        <span>
                                            W-D-L of the match when that quarter went our way
                                        </span>
                                    </div>
                                    <div className="insightsTableScroll">
                                        <table className="insightsPlayerTable">
                                            <thead>
                                                <tr>
                                                    <th>Period</th>
                                                    <th>Won</th>
                                                    <th>Drew</th>
                                                    <th>Lost</th>
                                                    <th>When won Q</th>
                                                    <th>When lost Q</th>
                                                    <th>When drew Q</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {quarterControl.map((row) => (
                                                    <tr key={row.label}>
                                                        <td>{row.label}</td>
                                                        <td>
                                                            {row.won}/{row.played}
                                                        </td>
                                                        <td>{row.drawn}</td>
                                                        <td>{row.lost}</td>
                                                        <td>{wdlText(row.whenWon)}</td>
                                                        <td>{wdlText(row.whenLost)}</td>
                                                        <td>{wdlText(row.whenDrawn)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="insightsTableNote">
                                        Quarter score from logged goals in that period. Periods with
                                        no goals are skipped.
                                    </p>
                                </section>
                            ) : null}
                        </div>
                    ) : null}

                    <section className="insightsPanel">
                        <div className="insightsPanelHead">
                            <h2>Home and away</h2>
                            <span>Finished matches</span>
                        </div>
                        {!venue ? (
                            <div className="insightsEmpty">No finished matches yet.</div>
                        ) : (
                            <div className="insightsTableCluster insightsTableClusterInner">
                                <table className="insightsPlayerTable insightsTableFit">
                                    <thead>
                                        <tr>
                                            <th>Venue</th>
                                            <th>P</th>
                                            <th>W</th>
                                            <th>D</th>
                                            <th>L</th>
                                            <th>GF/g</th>
                                            <th>GA/g</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { label: 'Home', summary: venue.home },
                                            { label: 'Away', summary: venue.away },
                                        ].map((row) => (
                                            <tr key={row.label}>
                                                <td>{row.label}</td>
                                                <td>{row.summary.played}</td>
                                                <td>{row.summary.wins}</td>
                                                <td>{row.summary.draws}</td>
                                                <td>{row.summary.losses}</td>
                                                <td>
                                                    {perGame(
                                                        row.summary.goalsFor,
                                                        row.summary.played
                                                    )}
                                                </td>
                                                <td>
                                                    {perGame(
                                                        row.summary.goalsAgainst,
                                                        row.summary.played
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {venueEvents.length > 0 ? (
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Metric</th>
                                                <th>Home</th>
                                                <th>Away</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {venueEvents.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{row.label}</td>
                                                    <td>{row.left}</td>
                                                    <td>{row.right}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : null}
                            </div>
                        )}
                    </section>

                    {whyWeWin.length > 0 || showGameState ? (
                        <div className="insightsTableCluster">
                            {whyWeWin.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Why we win</h2>
                                        <span>Wins vs losses in the sample</span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Metric</th>
                                                <th>Wins</th>
                                                <th>Losses</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {whyWeWin.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{row.label}</td>
                                                    <td>{row.wins}</td>
                                                    <td>{row.losses}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            ) : null}

                            {showGameState ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Game state</h2>
                                        <span>
                                            Our shots by score · clutch = last 5′ and within 2
                                        </span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>State</th>
                                                <th>Goals</th>
                                                <th>Shots</th>
                                                <th>%</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(
                                                [
                                                    ['Leading', gameState.leading],
                                                    ['Tied', gameState.tied],
                                                    ['Trailing', gameState.trailing],
                                                    ['Clutch', gameState.clutch],
                                                ] as const
                                            ).map(([label, bucket]) => (
                                                <tr key={label}>
                                                    <td>{label}</td>
                                                    <td>{bucket.goals}</td>
                                                    <td>{bucket.shots}</td>
                                                    <td>
                                                        {shootPct(bucket) == null
                                                            ? '-'
                                                            : `${shootPct(bucket)}%`}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            ) : null}
                        </div>
                    ) : null}

                    {recentForm.length > 0 || correlates.length > 0 ? (
                        <div className="insightsTableCluster">
                            {recentForm.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>Last 10 vs filtered</h2>
                                        <span>Pooled rates, not an average of averages</span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Metric</th>
                                                <th>Filtered</th>
                                                <th>Last 10</th>
                                                <th>Δ</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentForm.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{row.label}</td>
                                                    <td>{row.sample}</td>
                                                    <td>{row.recent}</td>
                                                    <td>
                                                        <span
                                                            className={`insightsFormDelta ${formDeltaClass(
                                                                row
                                                            )}`}
                                                        >
                                                            {formatFormDelta(row)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </section>
                            ) : null}

                            {correlates.length > 0 ? (
                                <section className="insightsPanel">
                                    <div className="insightsPanelHead">
                                        <h2>When this is high</h2>
                                        <span>Match W-D-L at/above vs below sample median</span>
                                    </div>
                                    <table className="insightsPlayerTable insightsTableFit">
                                        <thead>
                                            <tr>
                                                <th>Metric</th>
                                                <th>High</th>
                                                <th>Low</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {correlates.map((row) => (
                                                <tr key={row.label}>
                                                    <td>{row.label}</td>
                                                    <td>
                                                        {row.high} ({row.nHigh})
                                                    </td>
                                                    <td>
                                                        {row.low} ({row.nLow})
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <p className="insightsTableNote">
                                        Small samples lie. This is a split, not a cause.
                                    </p>
                                </section>
                            ) : null}
                        </div>
                    ) : null}

                    {filteredBundles.length > 0 ? (
                        <section className="insightsPanel">
                            <div className="insightsPanelHead">
                                <h2>Trend</h2>
                                <div className="insightsRateToggle" role="group">
                                    {TEAM_TREND_METRICS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={trendMetric === opt.value ? 'isActive' : ''}
                                            onClick={() => setTrendMetric(opt.value)}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <InsightsPlayerTrendChart
                                points={trendSeries}
                                valueLabel={
                                    TEAM_TREND_METRICS.find((opt) => opt.value === trendMetric)
                                        ?.label
                                }
                                percentScale={isPercentTrend(trendMetric)}
                            />
                        </section>
                    ) : null}
                </Spin>
            </div>
        </div>
    );
};

export default InsightsTeam;
